import api from './apiClient'

/**
 * Relatórios do Épico 09 — `GET /reports/*` na nova API.
 *
 * Este módulo era um gerador de dados falsos. Não é mais: cada função abaixo
 * consulta o banco de produção, e o que o banco não sustenta volta como `null`
 * com o motivo em `unavailable[]`, exatamente como no dashboard.
 *
 * A diferença entre os dois campos que a API devolve importa na tela:
 *  - `unavailable: [{ indicator, reason }]` → o indicador NÃO EXISTE. Mostrar
 *    "—" com o motivo no Tooltip.
 *  - `notes: [{ code, message, affected }]` → o número existe, mas engana se
 *    lido sem contexto (quantidade herdada do legado, tempo de dado migrado).
 *    Mostrar como aviso ao lado do relatório, sem esconder o número.
 */

/**
 * Traduz os filtros da tela para a query da API.
 *
 * `startDate`/`finishDate` viram `from`/`to`; a API fecha a data final no fim do
 * dia. Campo vazio é omitido — mandar string vazia faria o zod recusar.
 */
const parametros = (filtros = {}, extras = {}) => {
  const query = {
    subCompanyId: filtros.subCompanyId || undefined,
    from: filtros.startDate || undefined,
    to: filtros.finishDate || undefined,
    limit: filtros.limit || 10,
    offset: filtros.offset || 0,
  }

  Object.entries(extras).forEach(([chave, valor]) => {
    if (valor !== '' && valor !== null && valor !== undefined) query[chave] = valor
  })

  return query
}

/** Envelope comum dos quatro relatórios. */
const consultar = async (rota, query) => {
  const resposta = await api.get(rota, { params: query })
  const corpo = resposta.data || {}
  return {
    items: corpo.items || [],
    total: corpo.total || 0,
    summary: corpo.summary || null,
    notes: corpo.notes || [],
    unavailable: corpo.unavailable || [],
  }
}

/** Ordens produzidas no período, com quantidades, tempo e eficiência. */
export const getProductionReport = filtros =>
  consultar(
    '/reports/production',
    parametros(filtros, { status: filtros.orderStatus, search: filtros.search })
  )

/** Etiquetas: criadas, impressas, pendentes, canceladas, perdidas, produzidas. */
export const getRfidReport = filtros =>
  consultar(
    '/reports/rfid',
    parametros(filtros, {
      situation: filtros.situation,
      epc: filtros.epc,
      orderNumber: filtros.orderNumber,
    })
  )

/** Quem produziu, quanto, em que etapa, em que horário e em quanto tempo. */
export const getOperatorReport = filtros =>
  consultar(
    '/reports/operator',
    parametros(filtros, { operator: filtros.operator, stage: filtros.stage })
  )

/** Toda a movimentação registrada da etiqueta. */
export const getHistoryReport = filtros =>
  consultar(
    '/reports/history',
    parametros(filtros, {
      epc: filtros.epc,
      eventType: filtros.eventType,
      source: filtros.source,
    })
  )

/**
 * Linha do tempo de uma etiqueta: o mesmo histórico, filtrado por EPC.
 *
 * Não existe rota dedicada — e não deveria existir: é o relatório de histórico
 * com um filtro, e duplicá-lo no back-end criaria duas verdades sobre o mesmo
 * evento.
 */
export const getRfidTimeline = (epc, filtros = {}) =>
  getHistoryReport({ ...filtros, epc, limit: filtros.limit || 100, offset: 0 })

/** `unavailable[]` → mapa `indicador → motivo`, para o Tooltip da célula. */
export const motivosIndisponiveis = unavailable =>
  (unavailable || []).reduce((mapa, item) => ({ ...mapa, [item.indicator]: item.reason }), {})
